import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider, FormControl, InputAdornment,
  InputLabel, LinearProgress, List, ListItemButton, ListItemText, MenuItem,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FilterList, Search } from "@mui/icons-material";
import { Navigate, useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../design-system/components";
import usePageTitle from "../hooks/usePageTitle";
import {
  buscarArbitros, getCompeticionesArbitros, getRankingArbitros,
} from "../services/arbitroEstadisticasService";
import { normalizeRefereeCompetitions } from "../helpers/arbitroEstadisticasHelper";
import DirectoryPage from "../components/directories/DirectoryPage";
import DirectoryPagination from "../components/directories/DirectoryPagination";
import DirectorySavedSearches, { DirectoryColumns, useDirectoryColumns } from "../components/directories/DirectoryPreferences";
import RefereeCompetitionPanel from "../components/directories/RefereeCompetitionPanel";

const RANKING_COLUMNS = [
  ["posicion", null],
  ["nombre", "ranking.name"],
  ["partidos", "ranking.matches"],
  ["amarillas", "cards.yellow"],
  ["segundasAmarillas", "cards.secondYellow"],
  ["rojas", "cards.red"],
  ["puntosDisciplinarios", "cards.points"],
  ["amarillasPorPartido", "ranking.rateYellow"],
  ["segundasAmarillasPorPartido", "ranking.rateSecondYellow"],
  ["rojasPorPartido", "ranking.rateRed"],
  ["tarjetasPorPartido", "ranking.rateTotal"],
];

const RefereeRanking = () => {
  const { t, i18n } = useTranslation("referees");
  const { t: d } = useTranslation("directories");
  usePageTitle(t("title"));
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const competicionId = searchParams.get("idCompeticion") || "";
  const season = searchParams.get("idTemporada") || "";
  const comparison = searchParams.get("compare") || "";
  const [columns, setColumns] = useDirectoryColumns("referees", RANKING_COLUMNS.map(([id]) => id), RANKING_COLUMNS.map(([id]) => id));
  const shownColumns = RANKING_COLUMNS.filter(([id]) => ["nombre", "posicion"].includes(id) || columns.includes(id));
  const submittedQuery = searchParams.get("q")?.trim() || "";
  const [competiciones, setCompeticiones] = useState([]);
  const [competicionesError, setCompeticionesError] = useState(false);
  const [query, setQuery] = useState(submittedQuery);
  const [validationError, setValidationError] = useState(false);
  const [searchRetry, setSearchRetry] = useState(0);
  const [search, setSearch] = useState({ key: "", status: "idle", results: [] });
  const [ranking, setRanking] = useState({ key: "", status: "loading", data: [] });
  const [rankingRetry, setRankingRetry] = useState(0);
  const rankingOrder = RANKING_COLUMNS.some(([id]) => id === searchParams.get("sort"))
    ? searchParams.get("sort") : "puntosDisciplinarios";
  const rankingDirection = searchParams.get("direction") === "asc" ? "asc" : "desc";
  const minimumMatches = Math.max(0, Number(searchParams.get("minMatches")) || 0);
  const mobile = useMediaQuery("(max-width:699px)");
  const headingRef = useRef(null);
  const searchKey = JSON.stringify([submittedQuery, competicionId, season, searchRetry]);
  const rankingKey = JSON.stringify([competicionId, season, rankingRetry]);
  const searching = submittedQuery.length >= 2 &&
    (search.key !== searchKey || search.status === "loading");
  const results = search.key === searchKey ? search.results : [];
  const searchError = search.key === searchKey && search.status === "error";
  const loadingRanking = ranking.key !== rankingKey || ranking.status === "loading";
  const rankingError = ranking.key === rankingKey && ranking.status === "error";
  const numFmt = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 2 }),
    [i18n.resolvedLanguage, i18n.language]
  );
  const competitionOptions = useMemo(
    () => normalizeRefereeCompetitions(competiciones, {
      deduplicateNames: true, selectedId: competicionId,
    }).filter((competition) => competition.nombre),
    [competiciones, competicionId]
  );
  const seasons = [...new Set(competiciones.filter((item) => item && (!competicionId || String(item.idCompeticion) === competicionId))
    .flatMap((item) => Array.isArray(item.temporadas) ? item.temporadas : []).map(String))].sort((a, b) => Number(b) - Number(a));
  const sortedRanking = useMemo(
    () => ranking.data.filter((referee) => Number(referee.partidos) >= minimumMatches).sort((a, b) => {
      const comparison = rankingOrder === "nombre"
        ? String(a.nombre ?? "").localeCompare(String(b.nombre ?? ""), i18n.language)
        : Number(a[rankingOrder] || 0) - Number(b[rankingOrder] || 0);
      return rankingDirection === "asc" ? comparison : -comparison;
    }),
    [ranking.data, rankingOrder, rankingDirection, minimumMatches, i18n.language]
  );
  const pageSize = 25;
  const page = Math.min(Math.max(1, Math.floor(Number(searchParams.get("page")) || 1)), Math.max(1, Math.ceil(sortedRanking.length / pageSize)));
  const visibleRanking = sortedRanking.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (navigationType === "POP") headingRef.current?.focus();
  }, [location.key, navigationType]);

  useEffect(() => {
    let stale = false;
    getCompeticionesArbitros(navigate)
      .then((data) => {
        if (!stale) setCompeticiones(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!stale) setCompeticionesError(true);
      });
    return () => { stale = true; };
  }, [navigate]);

  useEffect(() => {
    let stale = false;
    setRanking({ key: rankingKey, status: "loading", data: [] });
    (season ? getRankingArbitros(navigate, competicionId, season) : getRankingArbitros(navigate, competicionId))
      .then((data) => {
        if (!stale) setRanking({
          key: rankingKey, status: "success", data: Array.isArray(data) ? data : [],
        });
      })
      .catch(() => {
        if (!stale) setRanking({ key: rankingKey, status: "error", data: [] });
      });
    return () => { stale = true; };
  }, [navigate, competicionId, season, rankingKey]);

  useEffect(() => {
    setQuery(submittedQuery);
    setValidationError(false);
  }, [submittedQuery, competicionId]);

  useEffect(() => {
    if (submittedQuery.length < 2) {
      setSearch({ key: searchKey, status: "idle", results: [] });
      return;
    }
    let stale = false;
    setSearch({ key: searchKey, status: "loading", results: [] });
    (season ? buscarArbitros(submittedQuery, navigate, competicionId, season) : buscarArbitros(submittedQuery, navigate, competicionId))
      .then((data) => {
        if (!stale) setSearch({
          key: searchKey, status: "success", results: Array.isArray(data) ? data : [],
        });
      })
      .catch(() => {
        if (!stale) setSearch({ key: searchKey, status: "error", results: [] });
      });
    return () => { stale = true; };
  }, [submittedQuery, competicionId, season, navigate, searchKey]);

  const handleSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    if (value.length < 2) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    if (value === submittedQuery) setSearchRetry((retry) => retry + 1);
    else setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("q", value);
      next.delete("page");
      next.delete("compare");
      return next;
    });
  };

  const handleCompetitionChange = (event) => {
    const value = event.target.value;
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set("idCompeticion", value);
      else next.delete("idCompeticion");
      next.delete("page");
      next.delete("compare");
      next.delete("idTemporada");
      return next;
    });
  };

  const handleSelectReferee = (idArbitro) => {
    const suffix = searchParams.toString() ? `?${searchParams}` : "";
    navigate(`/estadisticasArbitros/${encodeURIComponent(idArbitro)}${suffix}`, {
      state: { refereeRankingUrl: `/estadisticasArbitros${suffix}` },
    });
  };

  const handleRankingSort = (column) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("sort", column);
      next.delete("page");
      next.set("direction", rankingOrder === column && rankingDirection === "desc" ? "asc" : "desc");
      return next;
    }, { replace: true });
  };

  return (
    <DirectoryPage title={t("title")} subtitle={t("subtitle")} headingRef={headingRef}>
      <Stack spacing={3}>
        <Card className="dr-panel" sx={{ border: 1, borderColor: "divider" }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <FilterList color="action" aria-hidden="true" />
              <FormControl size="small" sx={{ minWidth: { sm: 240 }, width: { xs: "100%", sm: "auto" }, maxWidth: "100%" }}>
                <InputLabel id="competicion-select-label">{t("competition.label")}</InputLabel>
                <Select
                  labelId="competicion-select-label"
                  value={competicionId}
                  onChange={handleCompetitionChange}
                  label={t("competition.label")}
                  sx={{ minHeight: 44 }}
                >
                  <MenuItem value="">{t("competition.all")}</MenuItem>
                  {competicionId && !competitionOptions.some((c) => String(c.idCompeticion) === competicionId) && (
                    <MenuItem value={competicionId}>{t("competition.unknown")}</MenuItem>
                  )}
                  {competitionOptions.map((competition) => (
                    <MenuItem key={competition.idCompeticion} value={String(competition.idCompeticion)}>
                      {competition.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {competicionesError && <Typography color="error" role="status">{t("competition.error")}</Typography>}
              <TextField select label={d("season")} value={season} SelectProps={{ native: true }}
                onChange={(event) => setSearchParams((previous) => {
                  const next = new URLSearchParams(previous);
                  if (event.target.value) next.set("idTemporada", event.target.value); else next.delete("idTemporada");
                  next.delete("page"); next.delete("compare");
                  return next;
                }, { replace: true })}>
                <option value="">{d("allSeasons")}</option>
                {season && !seasons.includes(season) && <option value={season}>{season}</option>}
                {seasons.map((value) => <option key={value} value={value}>{value}</option>)}
              </TextField>
              <TextField type="number" label={t("ranking.minimum")} value={searchParams.get("minMatches") || ""}
                placeholder="0" inputProps={{ min: 0, step: 1 }}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchParams((previous) => {
                    const next = new URLSearchParams(previous);
                    if (value) next.set("minMatches", value); else next.delete("minMatches");
                    next.delete("page");
                    return next;
                  }, { replace: true });
                }} />
            </Stack>
          </CardContent>
        </Card>
        <DirectorySavedSearches scope="referees" />
        <RefereeCompetitionPanel competition={competicionId} season={season} referee={comparison} />

        <Card className="dr-panel" sx={{ border: 1, borderColor: "divider" }}>
          <CardContent>
            <Box component="form" onSubmit={handleSearch}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth label={t("search.label")} placeholder={t("search.placeholder")}
                  value={query} onChange={(event) => setQuery(event.target.value)}
                  error={validationError}
                  helperText={validationError ? t("search.minimum") : undefined}
                  inputProps={{ maxLength: 80 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search aria-hidden="true" /></InputAdornment> }}
                />
                <Button
                  type="submit" variant="contained" disabled={searching}
                  startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <Search />}
                  sx={{ minHeight: 48, minWidth: { sm: 140 } }}
                >
                  {t("search.action")}
                </Button>
              </Stack>
            </Box>
            {searchError && <Alert severity="error" sx={{ mt: 2 }}>{t("search.error")}</Alert>}
            {searching && <Typography role="status" color="text.secondary" sx={{ mt: 2 }}>{t("search.loading")}</Typography>}
            {!searching && results.length > 0 && (
              <List aria-label={t("search.results")} sx={{ mt: 2, border: 1, borderColor: "divider", borderRadius: 2, p: 0 }}>
                {results.map((referee, index) => (
                  <React.Fragment key={referee.idArbitro}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleSelectReferee(referee.idArbitro)} sx={{ py: 1.25, minHeight: 44 }}>
                      <ListItemText
                        primary={referee.nombre || t("profile")}
                        secondary={t("search.context", { seasons: referee.temporadas?.length ?? 0 })}
                        primaryTypographyProps={{ fontWeight: 700, sx: { overflowWrap: "anywhere" } }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
            {!searching && search.key === searchKey && search.status === "success" && results.length === 0 && (
              <Typography role="status" color="text.secondary" sx={{ mt: 2 }}>{t("search.empty")}</Typography>
            )}
          </CardContent>
        </Card>

        {!loadingRanking && !rankingError && <div className="dr-stats-strip">
          {[
            [t("ranking.referees"), sortedRanking.length],
            [t("ranking.matches"), sortedRanking.reduce((sum, item) => sum + Number(item.partidos || 0), 0)],
            [t("metrics.cardsPerMatch"), (() => {
              const matches = sortedRanking.reduce((sum, item) => sum + Number(item.partidos || 0), 0);
              const cards = sortedRanking.reduce((sum, item) => sum + Number(item.amarillas || 0) + Number(item.segundasAmarillas || 0) + Number(item.rojas || 0), 0);
              return matches ? numFmt.format(cards / matches) : "—";
            })()],
          ].map(([label, value]) => <section className="dr-panel" key={label}><p>{label}</p><strong>{value}</strong></section>)}
        </div>}
        <Card className="dr-panel" sx={{ border: 1, borderColor: "divider", minWidth: 0 }}>
          <CardContent sx={{ minWidth: 0 }}>
            <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>{t("ranking.title")}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>{t("ranking.description")}</Typography>
            <DirectoryColumns options={RANKING_COLUMNS.filter(([id]) => !["nombre", "posicion"].includes(id)).map(([id, label]) => [id, t(label)])}
              value={columns} onChange={setColumns} />
            {loadingRanking && <LinearProgress aria-label={t("ranking.loading")} />}
            {rankingError && (
              <Stack spacing={1} alignItems="flex-start">
                <Alert severity="error">{t("ranking.error")}</Alert>
                <Button variant="outlined" onClick={() => setRankingRetry((retry) => retry + 1)} sx={{ minHeight: 44 }}>{t("retry")}</Button>
              </Stack>
            )}
            {!loadingRanking && !rankingError && !mobile && (
              <TableContainer tabIndex={0} role="region" aria-label={t("ranking.title")} sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <Table size="small" aria-label={t("ranking.title")} sx={{ "& td, & th": { fontVariantNumeric: "tabular-nums" } }}>
                  <TableHead>
                    <TableRow>
                      {shownColumns.map(([id, label]) => (
                        <TableCell key={id} align={id === "nombre" ? "left" : "right"} sortDirection={rankingOrder === id ? rankingDirection : false}>
                          <TableSortLabel
                            active={rankingOrder === id}
                            direction={rankingOrder === id ? rankingDirection : "asc"}
                            onClick={() => handleRankingSort(id)}
                            sx={{ minHeight: 44 }}
                          >
                            {label ? t(label) : "#"}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRanking.map((referee) => (
                      <TableRow key={referee.idArbitro} hover>
                        {shownColumns.map(([id]) => (
                          <TableCell key={id} align={id === "nombre" ? "left" : "right"} sx={id === "nombre" ? { p: 0 } : undefined}>
                            {id === "nombre" ? (
                              <><Button
                                variant="text" onClick={() => handleSelectReferee(referee.idArbitro)}
                                sx={{
                                  minHeight: 44, justifyContent: "flex-start", fontWeight: 700, textTransform: "none",
                                  textAlign: "left", px: 1.5, width: "100%",
                                  "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                                }}
                              >
                                {referee.nombre || t("profile")}
                              </Button><Button disabled={!season || !competicionId} aria-label={d("compareReferee", { name: referee.nombre })}
                                onClick={() => setSearchParams((previous) => {
                                  const next = new URLSearchParams(previous);
                                  next.set("compare", String(referee.idArbitro));
                                  return next;
                                }, { replace: true })}>{d("compare")}</Button></>
                            ) : numFmt.format(Number(referee[id]) || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {sortedRanking.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={shownColumns.length} align="center">
                          <Typography role="status" color="text.secondary" py={2}>{t("ranking.empty")}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!loadingRanking && !rankingError && mobile && <div className="dr-stack">
              <TextField select label={t("ranking.sort")} value={rankingOrder} SelectProps={{ native: true }}
                onChange={(event) => handleRankingSort(event.target.value)}>
                {RANKING_COLUMNS.map(([id, label]) => <option value={id} key={id}>{label ? t(label) : "#"}</option>)}
              </TextField>
              <Button onClick={() => handleRankingSort(rankingOrder)}>{t("ranking.direction")} {rankingDirection === "asc" ? "↑" : "↓"}</Button>
              {visibleRanking.map((referee) => <article className="dr-player-card" key={referee.idArbitro}>
                <Button onClick={() => handleSelectReferee(referee.idArbitro)} sx={{ textAlign: "left", justifyContent: "flex-start" }}>
                  {referee.nombre || t("profile")} →
                </Button>
                <Button disabled={!season || !competicionId} aria-label={d("compareReferee", { name: referee.nombre })}
                  onClick={() => setSearchParams((previous) => {
                    const next = new URLSearchParams(previous); next.set("compare", String(referee.idArbitro)); return next;
                  }, { replace: true })}>{d("compare")}</Button>
                <dl className="dr-kpis">{shownColumns.filter(([id]) => !["posicion", "nombre"].includes(id)).map(([id, label]) =>
                  <div key={id}><dt>{t(label)}</dt><dd>{numFmt.format(Number(referee[id]) || 0)}</dd></div>)}</dl>
              </article>)}
              {!sortedRanking.length && <p role="status">{t("ranking.empty")}</p>}
            </div>}
            {!loadingRanking && !rankingError && <DirectoryPagination page={page} pageSize={pageSize} total={sortedRanking.length}
              onChange={(value) => setSearchParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("page", String(value));
                return next;
              }, { replace: true })} />}
          </CardContent>
        </Card>
      </Stack>
      <p className="dr-note">{t("ranking.sampleNote")}</p>
    </DirectoryPage>
  );
};

const EstadisticasArbitrosPageModern = () => {
  const [searchParams] = useSearchParams();
  const legacyId = searchParams.get("idArbitro");
  if (legacyId) {
    const next = new URLSearchParams(searchParams);
    next.delete("idArbitro");
    return <Navigate to={`/estadisticasArbitros/${encodeURIComponent(legacyId)}${next.toString() ? `?${next}` : ""}`} replace />;
  }
  return <RefereeRanking />;
};

export default EstadisticasArbitrosPageModern;
